import { ZuiBaseElement } from '@zywave/zui-base';
import { html, svg } from 'lit';
import { property, query } from 'lit/decorators.js';
import { style } from './zui-svg-css.js';
import { ifDefined } from 'lit/directives/if-defined.js';

/**
 * Created mostly to support `<zui-icon>` but can be used to render native `.svg` files, and `.svg` spritesheets in tandem with `svg-id` / `svgId`.
 *
 * @prop {string} [viewBox=(variable)] - `viewBox` determines an `*.svg` file's canvas size; this value in most situations is automatically found and set but if your graphics aren't displaying or scaling correctly, copy and supply the `viewBox="0` `0` `#` `#"` from your `<svg>`
 *
 * @attribute {string} [svg-id=(variable)] - `viewBox` determines an `*.svg` file's canvas size; this value in most situations is automatically found and set but if your graphics aren't displaying or scaling correctly, copy and supply the `viewBox="0` `0` `#` `#"`  from your `<svg>`
 *
 * @cssprop [--zui-svg-width=1rem (16px)] - Width of component
 * @cssprop [--zui-svg-height=1rem (16px)] - Height of component
 */
export class ZuiSvg extends ZuiBaseElement {
  /**
   * Only applicable to svg sprites, supply an id for which `<symbol>` to show i.e. `id="#graphic-name"`
   */
  @property({ type: String, attribute: 'svg-id' }) svgId = '';

  /**
   * Link to external `*.svg` file
   */
  @property({ type: String, attribute: 'file' }) file = '';

  @property({ type: String, attribute: 'viewBox' }) viewBox: string;

  @query('svg') _svg: SVGElement;

  private svgNodeClone: Node | null = null;

  static get styles() {
    return [super.styles, style];
  }

  render() {
    if (!this.svgId) {
      return html`
        <style>
          :host {
            display: none;
          }
        </style>
      `;
    }

    this.svgNodeClone = undefined;
    const svgNode = !this.file ? document.getElementById(this.svgId) : undefined;
    if (svgNode) {
      this.svgNodeClone = svgNode.cloneNode(true);
    }

    let viewBox = this.viewBox;
    if (!viewBox && this.svgNodeClone) {
      viewBox = (this.svgNodeClone as HTMLElement).getAttribute('viewBox');
    }

    const href = `${this.file}#${this.svgId}`;
    return html`
      ${this.svgNodeClone
        ? html` <svg focusable="false" class="svg-defs"><defs>${this.svgNodeClone}</defs></svg> `
        : html``}
      ${svg`<svg viewBox="${ifDefined(viewBox)}" focusable="false" class="svg" id="svg">
        <use href="${href}" xlink:href="${href}"></use>
      </svg>`}
    `;
  }
}

window.customElements.define('zui-svg', ZuiSvg);

declare global {
  interface HTMLElementTagNameMap {
    'zui-svg': ZuiSvg;
  }
}
